import { handleActions, combineActions } from 'redux-actions'
import { Map, Set } from 'immutable'
import { keyBy as keyById } from 'utils/immutable'
import { combineReducers } from 'redux-immutable'
import * as actions from './actions'
import * as constants from './constants'
import stringify from 'fast-json-stable-stringify'

const selectedConversations = handleActions({
  [actions.selectConversation]: (state, action) => {
    const { conversationId } = action.payload
    return state.add(conversationId)
  },
  [actions.deselectConversation]: (state, action) => {
    const { conversationId } = action.payload
    return state.delete(conversationId)
  },
  [actions.selectConversations]: (state, action) => {
    const { conversationIds } = action.payload
    return state.union(conversationIds)
  },
  [actions.deselectConversations]: (state, action) => {
    const { conversationIds } = action.payload
    return state.subtract(conversationIds)
  },
  [actions.deselectAllConversations]: (state) => {
    return state.clear()
  },
}, Set())

const composeModalStatus = handleActions({
  [actions.setComposeModalStatus]: (state, action) => {
    const { status, endUserId, endUser, composeType, companyName, page } = action.payload
    const eid = (endUser && endUser.id) || endUserId
    const type = composeType || 'new_conversation'
    return { status, endUserId: status && eid, endUser, composeType: type, companyName: companyName, page: page }
  },
}, false)

const byId = handleActions({
  [actions.fetchConversationPending]: (state, action) => {
    const { conversationId } = action.payload

    return state.setIn([conversationId, 'status'], constants.FETCH_CONVERSATION_PENDING)
  },
  [actions.receiveConversation]: (state, action) => {
    const { conversation } = action.payload
    const conversationId = conversation.get('id')

    return state
      .setIn([conversationId, 'status'], constants.RECEIVE_CONVERSATION)
      .setIn([conversationId, 'data'], conversation)
  },
  [actions.updateConversations]: (state, action) => {
    const { updates } = action.payload

    return state.withMutations(_state => {
      updates.forEach(update => {
        if (!_state.get(update.conversationId)) {
          return
        }

        _state
          .setIn([update.conversationId, 'data', 'status'], update.conversationStatus)
          .setIn([update.conversationId, 'data', 'participantIds'], update.participantIds)
      })
    })
  },
  [actions.fetchConversationFailed]: (state, action) => {
    const { conversationId, error } = action.payload

    return state
      .setIn([conversationId, 'status'], constants.FETCH_CONVERSATION_FAILED)
      .setIn([conversationId, 'error'], error)
  }
}, Map())

const endUserConversationsCounts = handleActions({
  [actions.fetchConversationsCountForEndUserIdSuccess]: (state, action) => {
    const { id, count } = action.payload
    return state.set(id, count)
  }
}, Map())

const playbookIdByConversationId = handleActions({
  [actions.setPlaybookIdForConversation]: (state, action) => {
    const { conversationId, playbookId } = action.payload
    return state.set(conversationId, playbookId)
  }
}, Map())

const activeConversationContactTabIndex = handleActions({
  [actions.setActiveConversationContactTabIndex]: (state, action) => {
    return action.payload
  }
}, 0)

const routingAuditLogStatusById = handleActions({
  [combineActions(
    actions.fetchRoutingAuditLogSuccess,
    actions.fetchRoutingAuditLogFailed,
    actions.fetchRoutingAuditLogPending
  )]: (state, action) => {
    return state.set(action.payload.routingGroupId, action.type)
  }
}, Map())

const routingAuditLogById = handleActions({
  [actions.fetchRoutingAuditLogSuccess]: (state, action) => {
    return state.set(action.payload.routingGroupId, action.payload.data)
  }
}, Map())

const activeQueryByInboxId = handleActions({
  [actions.setQuery]: (state, action) => {
    const { conversationId, ...query } = action.payload
    const { inboxId } = query

    return state.set(inboxId, Map(query))
  },
}, Map())

const activeConversationByInboxId = handleActions({
  [actions.setActiveConversation]: (state, action) => {
    const { inboxId, conversationId } = action.payload

    return state.set(inboxId, conversationId)
  },
  [actions.unsetActiveConversation]: (state, action) => {
    const { inboxId } = action.payload

    return state.set(inboxId, null)
  },
}, Map())

const paginatedConversationsByQuery = handleActions({
  [actions.setQuery]: (state, action) => {
    const { conversationId, ...query } = action.payload

    if (!state.get(stringify(query))) {
      const newState = state
        .setIn([stringify(query), 'total'], 0)
        .setIn([stringify(query), 'cursor'], 0)
        .setIn([stringify(query), 'hasMore'], false)
        .setIn([stringify(query), 'loading'], true)

      return conversationId
        ? newState
        : newState
          .setIn([stringify(query), 'loaded'], false)
    }

    return state
  },
  [combineActions(
    actions.fetchConversationListSuccess,
    actions.fetchNextConversationListSuccess,
  )]: (state, action) => {
    const {
      query,
      paginatedConversations: {
        total,
        cursor,
        hasMore,
      },
    } = action.payload

    return state
      .setIn([stringify(query), 'total'], total)
      .setIn([stringify(query), 'cursor'], parseInt(cursor))
      .setIn([stringify(query), 'hasMore'], hasMore)
      .setIn([stringify(query), 'loaded'], true)
      .setIn([stringify(query), 'loading'], false)
  },
  [combineActions(
    actions.fetchConversationListPending,
  )]: (state, action) => {
    const { query } = action.payload

    return state.setIn([stringify(query), 'loading'], true)
  },
  [combineActions(
    actions.fetchConversationListFailed,
  )]: (state, action) => {
    const { query } = action.payload

    return state
      .setIn([stringify(query), 'loaded'], true)
      .setIn([stringify(query), 'loading'], false)
  },
}, Map())

// Assumes conversationIds are unique at the org level (and not inbox level)
const paginatedConversationDataById = handleActions({
  [combineActions(
    actions.fetchConversationListSuccess,
    actions.fetchNextConversationListSuccess,
  )]: (state, action) => {
    const {
      paginatedConversations: {
        data: conversations,
      },
    } = action.payload

    return state.mergeDeep(keyById(conversations))
  },
  [actions.receiveConversation]: (state, action) => {
    const { conversation } = action.payload

    const existingConversation = state.get(conversation.id)
    if (existingConversation) {
      if (conversation.updatedAt > existingConversation.updatedAt) {
        return state.set(conversation.id, conversation)
      } else {
        return state
      }
    } else {
      return state.set(conversation.id, conversation)
    }
  },
  [actions.updateConversations]: (state, action) => {
    const { updates } = action.payload

    return state.withMutations(_state => {
      updates.forEach(update => {
        if (!_state.get(update.conversationId)) {
          return
        }

        _state
          .setIn([update.conversationId, 'status'], update.conversationStatus)
          .setIn([update.conversationId, 'participantIds'], update.participantIds)
      })
    })
  }
}, Map())

const suggestedArticles = handleActions({
  [actions.fetchSuggestedArticlesSuccess]: (state, action) =>
    [...action.payload.suggestedActions]
}, [])

const globalAutoCloseConfigurationStatus = handleActions({
  [combineActions(
    actions.fetchGlobalAutoCloseConfigurationPending,
    actions.fetchGlobalAutoCloseConfigurationSuccess,
    actions.fetchGlobalAutoCloseConfigurationFailure,
  )] : (state, action) => {
    return state.set('fetch', action.type)
  },
  [combineActions(
    actions.updateGlobalAutoCloseConfigurationPending,
    actions.updateGlobalAutoCloseConfigurationSuccess,
    actions.updateGlobalAutoCloseConfigurationFailure,
  )] : (state, action) => {
    return state.set('update', action.type)
  }
}, Map())

const globalAutoCloseConfigurationData = handleActions({
  [combineActions(
    actions.fetchGlobalAutoCloseConfigurationSuccess,
    actions.updateGlobalAutoCloseConfigurationSuccess,
  )] : (state, action) => {
    return action.payload
  }
}, Map())

const globalAutoCloseConfiguration = combineReducers({
  status: globalAutoCloseConfigurationStatus,
  data: globalAutoCloseConfigurationData,
})

export default combineReducers({
  selectedConversations,
  composeModalStatus,
  activeConversationContactTabIndex,
  byId, // Full conversations
  endUserConversationsCounts,
  activeQueryByInboxId,
  activeConversationByInboxId,
  paginatedConversationsByQuery, // Paging, Loading, Ordered list
  paginatedConversationDataById, // Preview conversations
  playbookIdByConversationId,
  routingAuditLogStatusById,
  routingAuditLogById,
  suggestedArticles,
  globalAutoCloseConfiguration,
})



// WEBPACK FOOTER //
// ./src/scripts/app/modules/conversation/reducer.js